import { request } from 'lib/api'
import axios from 'axios'
import env from 'config/env'
import StateStorage from '../state-storage'
import { HTTP_METHODS } from './const'
import { configs as shopConfigs } from '../../config/shop-language.json'

const catalogRequests = new Map()
const desktopNavigationRequests = new Map()
const catalogRetryAfter = new Map()
const CATALOG_TIMEOUT = 25000
const CATALOG_RETRY_DELAY = 30000

const validLocale = (shop, language) =>
  shopConfigs.some(
    (config) =>
      config.slug === shop &&
      config.languages.some((item) => item.lang_slug === language),
  )

const validProductLineNavigation = (data) =>
  Array.isArray(data?.data) &&
  data.data.every((item) =>
    ['Women', 'Men', 'Kids'].includes(item?.attributes?.product_line),
  )

function validCatalog(data, shop, language) {
  return (
    data?.complete === true &&
    data.shop === shop &&
    data.language === language &&
    Number.isFinite(Date.parse(data.observedAt)) &&
    Number.isFinite(Date.parse(data.expiresAt)) &&
    Array.isArray(data.categories) &&
    Array.isArray(data.campaigns) &&
    data.categories.every(
      (category) =>
        Number.isSafeInteger(category.id) &&
        category.id > 0 &&
        typeof category.name === 'string' &&
        typeof category.slug === 'string' &&
        Number.isSafeInteger(category.totalProducts) &&
        category.totalProducts >= 0,
    )
  )
}

class Navigation {
  static getNavigationUrl = async () => `v7/navigations/categories`

  static async getDesktopLinks(shop, language) {
    if (!validLocale(shop, language)) {
      throw new Error('Unsupported navigation locale')
    }
    // Product-line navigation must never reuse the legacy mobile menu cache.
    const key = `pomeloDesktopProductLineNav-${shop}-${language}`
    const cached = StateStorage.getLocalState(key)
    if (
      cached?.shop === shop &&
      cached.language === language &&
      validProductLineNavigation(cached.links)
    )
      return cached.links
    if (desktopNavigationRequests.has(key))
      return desktopNavigationRequests.get(key)

    // Use an anonymous request here: the shared API helper retries401 with
    // account headers, which is inappropriate for a locale-keyed public cache.
    const pending = axios({
      baseURL: env.API_HOST,
      url: 'v7/navigations/categories',
      method: HTTP_METHODS.get,
      params: { bar_type: 'product_line' },
      timeout: env.API_REQUEST_TIMEOUT,
      withCredentials: false,
      // Fix the request locale even if a country switch updates StateStorage
      // before it finishes. No account identity is needed for public navigation.
      headers: {
        Accept: 'application/json',
        'Accept-Language':
          shop === language
            ? shop
            : `${language}-${shop === 'mo' ? 'hk' : shop}`,
        'x-platform': `web-desktop ${env.VERSION}`,
      },
    })
      .then(({ data: links }) => {
        if (!validProductLineNavigation(links))
          throw new Error('Invalid product-line navigation')
        try {
          StateStorage.saveLocalState(key, { shop, language, links }, 60)
        } catch (_) {
          // A storage failure must not hide a valid menu.
        }
        return links
      })
      .finally(() => desktopNavigationRequests.delete(key))
    desktopNavigationRequests.set(key, pending)
    return pending
  }

  static async getDesktopCatalog(shop, language) {
    if (!validLocale(shop, language)) {
      throw new Error('Unsupported catalog locale')
    }
    const key = `pomeloDesktopCatalog-${shop}-${language}`
    const cached = StateStorage.getLocalState(key)
    if (
      validCatalog(cached, shop, language) &&
      !cached.stale &&
      Date.parse(cached.expiresAt) > Date.now()
    ) {
      return cached
    }
    if (catalogRequests.has(key)) return catalogRequests.get(key)
    if (catalogRetryAfter.get(key) > Date.now()) {
      throw new Error('Catalog temporarily unavailable')
    }

    const controller =
      typeof AbortController === 'function' ? new AbortController() : null
    let timer
    let timedOut = false
    const pending = new Promise((resolve, reject) => {
      timer = setTimeout(() => {
        timedOut = true
        controller?.abort()
        reject(new Error('Catalog request timed out'))
      }, CATALOG_TIMEOUT)
      // This anonymous same-origin endpoint never receives account cookies.
      fetch(
        `/server/desktop-menu-catalog?shop=${encodeURIComponent(
          shop,
        )}&language=${encodeURIComponent(language)}`,
        {
          credentials: 'omit',
          // Old servers can redirect this route during a rolling deployment.
          // Use the validated snapshot cache above, not a retained HTTP redirect.
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          ...(controller && { signal: controller.signal }),
        },
      )
        .then((response) => {
          if (!response.ok) throw new Error('Catalog temporarily unavailable')
          return response.json()
        })
        .then((data) => {
          if (timedOut) throw new Error('Catalog request timed out')
          if (!validCatalog(data, shop, language)) {
            throw new Error('Invalid desktop catalog')
          }
          const lifespan = Math.min(
            600,
            Math.floor((Date.parse(data.expiresAt) - Date.now()) / 1000),
          )
          if (!data.stale && lifespan <= 0) {
            throw new Error('Expired desktop catalog')
          }
          if (!data.stale && lifespan > 0) {
            // Do not restart a server snapshot's ten-minute lifetime locally.
            try {
              StateStorage.saveLocalState(key, data, lifespan)
            } catch (_) {
              // Storage availability must not prevent using a valid response.
            }
          }
          resolve(data)
        }, reject)
        .catch(reject)
    })
      .catch((error) => {
        catalogRetryAfter.set(key, Date.now() + CATALOG_RETRY_DELAY)
        throw error
      })
      .finally(() => {
        clearTimeout(timer)
        catalogRequests.delete(key)
      })
    catalogRequests.set(key, pending)
    return pending
  }

  static async getCategoryTree() {
    const shop = StateStorage.getShop()
    const lang = StateStorage.getLanguage()
    const key = `pomeloDesktopCategoryTree-${shop}-${lang}`
    const cached = StateStorage.getLocalState(key)
    if (cached) return cached

    const response = await request({
      url: 'v6/categories',
      method: HTTP_METHODS.get,
    })
    if (!Array.isArray(response?.categories)) {
      throw new Error('Invalid category tree')
    }
    StateStorage.saveLocalState(key, response, 60)
    return response
  }

  static async getLinks(isMobile = true) {
    const lang = StateStorage.getLanguage()
    const shop = StateStorage.getShop()
    const keyName = `pomelo${isMobile ? 'Mobile' : 'Desktop'}Nav`
    const localStateKey = `${keyName}-${shop}-${lang}`
    const cached = StateStorage.getLocalState(localStateKey)

    if (cached) {
      return Promise.resolve(cached)
    }
    const url = await Navigation.getNavigationUrl()

    const res = await request({
      url,
      method: HTTP_METHODS.get,
      params: { bar_type: isMobile ? 'mobile' : 'web_top' },
    })

    StateStorage.saveLocalState(localStateKey, res, 60)

    return res
  }
}

export default Navigation
